import { ReactNode } from 'react';

import {
  RiDeleteBinLine,
  RiExternalLinkLine,
  RiFileLine,
  RiMore2Fill,
  RiPencilLine,
} from '@remixicon/react';

import {
  ButtonIcon,
  Link,
  Menu,
  MenuItem,
  MenuTrigger,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';

import {
  DateUtils,
  DialogComposite,
  Spinner,
  useDialog,
} from '@spotify/confidence-core-react';
import { useExternalDocumentRef } from '@spotify/confidence-external-documents-react';
import {
  AttachmentFragment,
  CollaborationV1ExternalDocumentSourceType,
  ExternalDocumentRefFragment,
} from '@spotify/confidence-graphql';
import {
  createArtifactExtension,
  createModifierClickHandler,
  isExternalDocument,
  isSafeUrl,
} from '@spotify/plugin-confidence-artifacts-react';
import { MarkdownContent } from '@spotify/plugin-confidence-chat-react';
import {
  ArtifactCardBase,
  ArtifactIcon,
  ArtifactListItem,
  getGradientStartColor,
  getResourceName,
} from '@spotify/plugin-confidence-projects-react';

import { projectsPlugin } from '../plugin';
import styles from './ExternalDocumentArtifactExtension.module.css';

const getSourceLabel = (
  type?: CollaborationV1ExternalDocumentSourceType,
): string => {
  switch (type) {
    case CollaborationV1ExternalDocumentSourceType.GoogleDocs:
      return 'Google Docs';
    case CollaborationV1ExternalDocumentSourceType.Notion:
      return 'Notion';
    default:
      return 'External document';
  }
};

const getSafeUrl = (url: string | null | undefined): string | undefined =>
  url && isSafeUrl(url) ? url : undefined;

const OpenInSourceButton = ({
  sourceUrl,
  sourceLabel,
}: {
  sourceUrl: string | undefined;
  sourceLabel: string;
}) => {
  if (!sourceUrl) {
    return null;
  }

  return (
    <TooltipTrigger>
      <ButtonIcon
        variant="tertiary"
        size="small"
        icon={<RiExternalLinkLine size={16} />}
        onPress={() => window.open(sourceUrl, '_blank', 'noopener,noreferrer')}
        aria-label={`Open in ${sourceLabel}`}
      />
      <Tooltip>{`Open in ${sourceLabel}`}</Tooltip>
    </TooltipTrigger>
  );
};

const ExternalDocumentMarkdownPreview = ({
  externalDocument,
  isLoading,
}: {
  externalDocument: ExternalDocumentRefFragment | undefined;
  isLoading: boolean;
}) => {
  if (isLoading && !externalDocument) {
    return (
      <div className={styles.loading}>
        <Spinner size={24} aria-label="Loading document" />
      </div>
    );
  }

  if (externalDocument?.markdown) {
    return <MarkdownContent>{externalDocument.markdown}</MarkdownContent>;
  }

  const safeSourceUrl = getSafeUrl(externalDocument?.source?.uri);
  const sourceLabel = getSourceLabel(externalDocument?.source?.type);

  return (
    <div className={styles.emptyState}>
      <Text variant="body-small" color="secondary">
        No preview available.
      </Text>
      {safeSourceUrl && (
        <Link href={safeSourceUrl} target="_blank" rel="noopener noreferrer">
          Open in {sourceLabel}
        </Link>
      )}
    </div>
  );
};

const ExternalDocumentContent = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => {
  const resourceName = getResourceName(artifact.uri);
  const { externalDocument, isLoading } = useExternalDocumentRef({
    name: resourceName,
  });

  return (
    <ExternalDocumentMarkdownPreview
      externalDocument={externalDocument}
      isLoading={isLoading}
    />
  );
};

const ExternalDocumentHeaderActions = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => {
  const resourceName = getResourceName(artifact.uri);
  const { externalDocument } = useExternalDocumentRef({ name: resourceName });
  const safeSourceUrl = getSafeUrl(externalDocument?.source?.uri);
  const sourceLabel = getSourceLabel(externalDocument?.source?.type);

  return (
    <OpenInSourceButton sourceUrl={safeSourceUrl} sourceLabel={sourceLabel} />
  );
};

const ExternalDocumentCard = ({
  artifact,
  onDelete,
}: {
  artifact: AttachmentFragment;
  onDelete?: () => void;
}) => {
  const { openDialog, closeDialog } = useDialog();
  const resourceName = getResourceName(artifact.uri);
  const { externalDocument } = useExternalDocumentRef({ name: resourceName });
  const safeSourceUrl = getSafeUrl(externalDocument?.source?.uri);
  const sourceLabel = getSourceLabel(externalDocument?.source?.type);

  const handleNavigate = () => {
    openDialog({
      content: (
        <DialogComposite.Root static onClose={closeDialog}>
          <DialogComposite.Content>
            <DialogComposite.CloseButton />
            <DialogComposite.Header>
              <DialogComposite.Title>
                {artifact.displayName ||
                  externalDocument?.title ||
                  'Untitled document'}
              </DialogComposite.Title>
            </DialogComposite.Header>
            <DialogComposite.Body className={styles.dialogBody}>
              <div className={styles.dialogActions}>
                <OpenInSourceButton
                  sourceUrl={safeSourceUrl}
                  sourceLabel={sourceLabel}
                />
              </div>
              <ExternalDocumentContent artifact={artifact} />
            </DialogComposite.Body>
          </DialogComposite.Content>
        </DialogComposite.Root>
      ),
      dialogProps: { maxWidth: 'md', fullWidth: true },
    });
  };

  return (
    <ArtifactCardBase
      artifact={artifact}
      resolvedDisplayName={externalDocument?.title ?? undefined}
      onNavigate={handleNavigate}
      onDelete={onDelete}
    />
  );
};

const ExternalDocumentListItem = ({
  artifact,
  icon,
  onSelect,
  onDelete,
  onRename,
}: {
  artifact: AttachmentFragment;
  icon: ReactNode;
  onSelect: () => void;
  onDelete?: () => void;
  onRename?: () => void;
}) => {
  const resourceName = getResourceName(artifact.uri);
  const { externalDocument } = useExternalDocumentRef({ name: resourceName });
  const safeSourceUrl = getSafeUrl(externalDocument?.source?.uri);
  const sourceLabel = getSourceLabel(externalDocument?.source?.type);
  const dateLabel = DateUtils.toDateString(
    new Date(artifact.createTime),
    DateUtils.MONTH_DAY_FORMAT,
  );

  const handleOpenExternal = () => {
    if (safeSourceUrl) {
      window.open(safeSourceUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <ArtifactListItem.Root
      onClick={createModifierClickHandler(safeSourceUrl, onSelect)}
    >
      <ArtifactListItem.Icon>
        <ArtifactIcon>{icon}</ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={
          <Text variant="body-medium" as="div" truncate>
            {artifact.displayName ||
              externalDocument?.title ||
              'Untitled document'}
          </Text>
        }
        secondary={
          <Text variant="body-x-small" as="div" color="secondary" truncate>
            {sourceLabel} &middot; {dateLabel}
          </Text>
        }
      />
      <ArtifactListItem.Action>
        <MenuTrigger>
          <ButtonIcon
            variant="tertiary"
            size="small"
            icon={<RiMore2Fill size={16} />}
            aria-label="Document actions"
          />
          <Menu placement="bottom end">
            <MenuItem iconStart={<RiFileLine />} onAction={onSelect}>
              Open document
            </MenuItem>
            {safeSourceUrl && (
              <MenuItem
                iconStart={<RiExternalLinkLine />}
                onAction={handleOpenExternal}
              >
                Open in {sourceLabel}
              </MenuItem>
            )}
            {onRename && (
              <MenuItem iconStart={<RiPencilLine />} onAction={onRename}>
                Rename
              </MenuItem>
            )}
            {onDelete && (
              <MenuItem
                color="danger"
                iconStart={<RiDeleteBinLine />}
                onAction={onDelete}
              >
                Delete
              </MenuItem>
            )}
          </Menu>
        </MenuTrigger>
      </ArtifactListItem.Action>
    </ArtifactListItem.Root>
  );
};

export const ExternalDocumentArtifactExtension = projectsPlugin.provide(
  createArtifactExtension({
    id: 'external-document',
    predicate: artifact => isExternalDocument(artifact.uri),
    icon: () => ({
      Icon: RiFileLine,
      color: getGradientStartColor('blue'),
    }),
    label: 'Document',
    category: 'Documents',
    render: artifact => <ExternalDocumentContent artifact={artifact} />,
    renderHeaderActions: artifact => (
      <ExternalDocumentHeaderActions artifact={artifact} />
    ),
    renderListItem: (artifact, { onSelect, onDelete, onRename, icon }) => (
      <ExternalDocumentListItem
        artifact={artifact}
        icon={icon}
        onSelect={onSelect}
        onDelete={onDelete}
        onRename={onRename}
      />
    ),
    renderCard: (artifact, { onDelete }) => (
      <ExternalDocumentCard artifact={artifact} onDelete={onDelete} />
    ),
  }),
);
